import {debounceTime, filter} from 'rxjs';
import {useEffect, useRef} from 'react';

import {css, view} from '@sail/ui/deprecated';

import type {
  Conversation,
  ConversationMessageAdded,
} from '@stripe-internal/conversations-client';
import {
  LoadingTextProvider,
  useConversationLogItems,
} from '@stripe-internal/conversations-ui';

import {DotComLoadingIndicator} from './DotComLoadingIndicator';
import {DotComLogItems} from './DotComLogItems';

type Props = {
  conversation: Conversation;
  animated?: boolean;
  disableInteraction?: boolean;
};

const supportsReverseColumn = () =>
  window?.CSS?.supports &&
  typeof window.CSS.supports === 'function' &&
  window.CSS.supports('overflow-anchor: auto');

const SCROLL_DELAY_MS = 16;

const scrollToAnchor = (anchor?: HTMLDivElement | null) => {
  setTimeout(() => {
    requestAnimationFrame(() => {
      anchor?.scrollIntoView({behavior: 'smooth'});
    });
  }, SCROLL_DELAY_MS);
};

function useEnsureIncomingMessagesInLog(conversation: Conversation) {
  useEffect(() => {
    const sub = conversation
      .eventsObservable()
      .pipe(
        filter(
          (event) =>
            event.name === 'conversation-message-added' &&
            event.message?.participant.role === 'agent',
        ),
        debounceTime(2000),
      )
      .subscribe((event) => {
        const messageAddedEvent = event as ConversationMessageAdded;
        const rawLog = conversation.log.getUnsorted();
        const id = messageAddedEvent.nonce ?? messageAddedEvent.messageId;
        const hasItem = !!rawLog[id];

        if (!hasItem) {
          const agentMessageCount = conversation.log
            .get()
            .filter(
              (item) =>
                item.name === 'conversation-message-added' &&
                item.message.participant.role === 'agent',
            ).length;
          const conversationHasMessageById = !!conversation.getMessage(
            messageAddedEvent.messageId,
          );
          const conversationHasMessageByNonce = !!(
            messageAddedEvent.nonce &&
            conversation.getMessage(messageAddedEvent.nonce)
          );
          const logHasMessageById = !!rawLog[messageAddedEvent.messageId];
          const logHasMessageByNonce = !!(
            messageAddedEvent.nonce && rawLog[messageAddedEvent.nonce]
          );

          conversation.client.errors.reportWarning(
            '[Log] appears message received may not be in log',
            {
              tags: {
                conversationId: conversation.id,
                messageId: id,
                agentMessageCount: agentMessageCount.toString(),
                conversationHasMessageById:
                  conversationHasMessageById.toString(),
                conversationHasMessageByNonce:
                  conversationHasMessageByNonce.toString(),
                logHasMessageById: logHasMessageById.toString(),
                logHasMessageByNonce: logHasMessageByNonce.toString(),
              },
            },
          );
        }
      });

    return () => sub.unsubscribe();
  }, [conversation]);
}

export function DotComLog({conversation, animated, disableInteraction}: Props) {
  const items = useConversationLogItems(conversation);
  const logRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEnsureIncomingMessagesInLog(conversation);

  useEffect(() => {
    if (supportsReverseColumn()) {
      return;
    }

    scrollToAnchor(anchorRef.current);
  }, [items]);

  const handleScroll = () => {
    const logContainer = logRef.current;

    if (
      logContainer &&
      logContainer.scrollTop > 0 &&
      logContainer.scrollTop <= 1
    ) {
      logContainer.scrollTop = 0;
    }
  };

  const logComponents = [
    <view.div
      key="anchor"
      ref={anchorRef}
      css={{
        flexGrow: css.value('1'),
        flexShrink: css.value('1'),
      }}
    />,
    <view.div
      key="typing-indicator"
      css={{'&:empty': {display: css.value('none')}}}
    >
      <DotComLoadingIndicator conversation={conversation} />
    </view.div>,
    <DotComLogItems
      key="log-items"
      ref={contentRef}
      conversation={conversation}
      items={items}
      animated={animated}
      disableInteraction={disableInteraction}
    />,
  ];

  let flexDirection: 'column-reverse' | 'column' = 'column-reverse';
  let children = logComponents;

  if (!supportsReverseColumn()) {
    flexDirection = 'column';
    children = [...logComponents].reverse();
  }

  return (
    <LoadingTextProvider>
      <view.div
        css={{flexGrow: css.value('1'), position: css.value('relative')}}
      >
        <view.div
          ref={logRef}
          onScroll={handleScroll}
          css={{
            display: css.value('flex'),
            flexDirection: css.value(flexDirection),
            overflowY: css.value('auto'),
            scrollbarWidth: css.value('none'),
            position: css.value('absolute'),
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,
            padding: 'medium',
            paddingBottom: 0,
            gap: 'medium',
          }}
        >
          {children}
        </view.div>
      </view.div>
    </LoadingTextProvider>
  );
}
